import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import LocalFireDepartmentRoundedIcon from "@mui/icons-material/LocalFireDepartmentRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import { useTranslation } from "react-i18next";
import { formatTime, windowHours } from "./helpers";

const CRIMSON = "#9b1c43";
const PINK_BG = "#fbe4ea";
const PINK_CHIP = "#f4cdd9";

const BoostRow = ({ icon, title, subtitle }) => (
  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 1 }}>
    <Box
      sx={{
        width: 34,
        height: 34,
        borderRadius: "50%",
        background: PINK_BG,
        color: CRIMSON,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography sx={{ fontWeight: 700, color: "#2b2b2b", lineHeight: 1.2 }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: 12, color: "#7a7a7a" }}>{subtitle}</Typography>
    </Box>
  </Stack>
);

const HappyHourTeaser = ({ open, onClose, status, onRemind }) => {
  const { t } = useTranslation();
  if (!status) return null;

  const multiplier = status.multiplier || status.coinMultiplier || 2;
  const coinMultiplier = status.coinMultiplier || multiplier;
  const streakMultiplier = status.streakMultiplier || 1;
  const hasBonusDrop = (status.bonusDrop || 0) > 0 || (status.cashDrop || 0) > 0;
  const hours = windowHours(status.startTime, status.endTime);
  const startLabel = formatTime(status.startTime);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      sx={{ zIndex: 10101 }}
      PaperProps={{
        sx: { borderRadius: 4, overflow: "hidden", background: "#fff" },
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{ position: "absolute", top: 8, right: 8, color: "#9a9a9a" }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ p: 0 }}>
        {/* Hero */}
        <Box
          sx={{
            background: PINK_BG,
            textAlign: "center",
            px: 3,
            py: 3.5,
          }}
        >
          <Typography
            sx={{
              color: CRIMSON,
              fontWeight: 800,
              fontSize: 12,
              letterSpacing: 1.5,
            }}
          >
            {t("happyHour.teaserKicker")}
          </Typography>
          <Typography sx={{ fontWeight: 800, fontSize: 26, color: "#1f1f1f" }}>
            {t("happyHour.teaserTitle")}
          </Typography>
          <Typography
            sx={{ color: CRIMSON, fontWeight: 900, fontSize: 56, lineHeight: 1.1 }}
          >
            {multiplier}x
          </Typography>
          <Typography sx={{ color: "#5a5a5a", fontSize: 14 }}>
            {t("happyHour.teaserSubtitle")}
          </Typography>
          <Box
            sx={{
              mt: 2,
              mx: "auto",
              display: "inline-block",
              background: PINK_CHIP,
              color: CRIMSON,
              fontWeight: 700,
              fontSize: 13,
              px: 2,
              py: 0.75,
              borderRadius: 5,
            }}
          >
            {t("happyHour.teaserStartsAt", { time: startLabel })}
            {hours > 0 ? t("happyHour.teaserWindowSuffix", { hours }) : ""}
          </Box>
        </Box>

        {/* What gets boosted */}
        <Box sx={{ px: 3, py: 2 }}>
          <Typography
            sx={{ fontSize: 12, color: "#9a9a9a", fontWeight: 700, mb: 0.5 }}
          >
            {t("happyHour.teaserBoostedHeading")}
          </Typography>

          <BoostRow
            icon={<BoltRoundedIcon fontSize="small" />}
            title={t("happyHour.teaserBoostCoinsTitle", {
              multiplier: coinMultiplier,
            })}
            subtitle={t("happyHour.teaserBoostCoinsSubtitle")}
          />
          <BoostRow
            icon={<LocalFireDepartmentRoundedIcon fontSize="small" />}
            title={t("happyHour.teaserBoostStreakTitle", {
              value: streakMultiplier > 1 ? streakMultiplier : 2,
            })}
            subtitle={t("happyHour.teaserBoostStreakSubtitle")}
          />
          {hasBonusDrop && (
            <BoostRow
              icon={<StarRoundedIcon fontSize="small" />}
              title={t("happyHour.teaserBoostDropTitle")}
              subtitle={t("happyHour.teaserBoostDropSubtitle")}
            />
          )}
        </Box>

        {/* Actions */}
        <Stack spacing={1.25} sx={{ px: 3, pb: 3 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => {
              if (onRemind)
                onRemind(t("happyHour.teaserRemindWhen", { time: startLabel }));
              onClose();
            }}
            sx={{
              borderColor: "#d9d9d9",
              color: "#1f1f1f",
              fontWeight: 700,
              textTransform: "none",
              borderRadius: 2,
              py: 1.1,
            }}
          >
            {t("happyHour.teaserRemindCta", { time: startLabel })}
          </Button>
          <Button
            fullWidth
            variant="text"
            onClick={onClose}
            sx={{
              color: "#7a7a7a",
              fontWeight: 600,
              textTransform: "none",
              border: "1px solid #ececec",
              borderRadius: 2,
              py: 1.1,
            }}
          >
            {t("happyHour.teaserMaybeLater")}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default HappyHourTeaser;
